/* eslint-disable react/no-unescaped-entities */
import {
    Box, Card, CardContent, styled, ToggleButton, ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { useState } from 'react';
import { DataObject, Settings } from '@mui/icons-material';
import { SetsWidgetsContainer } from './SetsWidgetsContainer';
import { SetsConfigJsonEditor } from './SetsConfigJsonEditor';

const StyledCard = styled(Card)(({ theme }) => ({
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(4),
    borderRadius: theme.spacing(2),
}));

const HeaderBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
}));

const StyledToggleButton = styled(ToggleButton)(({ theme }) => ({
    gap: theme.spacing(1),
}));

const StyledCardContent = styled(CardContent)({
    padding: 0,
    '&:last-child': {
        paddingBottom: 0,
    },
});

export enum VIEW_TYPES {
    WIDGETS = 'widgets',
    JSON = 'json',
}

interface SetsConfigCardProps {
    config: Models.SetsPlayground.SetsConfig | null;
    onConfigChange: (config: Models.SetsPlayground.SetsConfig) => void;
    onSetPriorityChange: (index: number, priority: number) => void;
    onAddSet: () => void;
    onDeleteSet: (index: number) => void;
    onSetNameChange: (index: number, name: string) => void;
    onSetDistributionChange: (index: number, dist: Models.SetsPlayground.SetDistribution) => void;
    onSetRankingChange: (index: number, ranking: Models.SetsPlayground.SetRanking) => void;
    onSetFilterChange: (index: number, filter: Models.SetsPlayground.SetFilterRequest) => void;
    onSetDesignPersonalizationContextIdChange: (index: number, id: string) => void;
    productKey?: string;
    onSetProductKeyChange?: (key: string) => void;
}

export const SetsConfigCard = ({
    config,
    onConfigChange,
    onSetPriorityChange,
    onAddSet, onDeleteSet,
    onSetNameChange,
    onSetDistributionChange,
    onSetRankingChange,
    onSetFilterChange,
    onSetDesignPersonalizationContextIdChange,
    productKey,
    onSetProductKeyChange,
}: SetsConfigCardProps): JSX.Element => {
    const [view, setView] = useState(VIEW_TYPES.WIDGETS);

    const handleViewChange = (_event: React.MouseEvent, newView: VIEW_TYPES): void => {
        if (newView) {
            setView(newView);
        }
    };
    const totalSets = config ? config.sets.length : 0;

    return (
        <StyledCard>
            <StyledCardContent>
                <HeaderBox>
                    <Typography variant="h6">
                        {totalSets > 0 ? `Sets Configuration (${totalSets} ${totalSets === 1 ? 'set' : 'sets'})` : 'Sets Configuration'}
                    </Typography>
                    <ToggleButtonGroup exclusive color="primary" size="small" value={view} onChange={handleViewChange}>
                        <StyledToggleButton value={VIEW_TYPES.WIDGETS}>
                            <Settings />
                            Widgets
                        </StyledToggleButton>
                        <StyledToggleButton value={VIEW_TYPES.JSON}>
                            <DataObject />
                            JSON
                        </StyledToggleButton>
                    </ToggleButtonGroup>
                </HeaderBox>
                <Typography color="text.secondary" variant="body2">
                    Create sets with distribution strategies (how items are selected), ranking methods
                    (how they're ordered), and filters (what gets excluded).
                    Each set has a priority that determines its execution order.
                </Typography>
                {view === VIEW_TYPES.WIDGETS ? (
                    <SetsWidgetsContainer
                        config={config}
                        productKey={productKey}
                        onAddSet={onAddSet}
                        onDeleteSet={onDeleteSet}
                        onSetDesignPersonalizationContextIdChange={onSetDesignPersonalizationContextIdChange}
                        onSetDistributionChange={onSetDistributionChange}
                        onSetFilterChange={onSetFilterChange}
                        onSetNameChange={onSetNameChange}
                        onSetPriorityChange={onSetPriorityChange}
                        onSetProductKeyChange={onSetProductKeyChange}
                        onSetRankingChange={onSetRankingChange}
                    />
                ) : (
                    <SetsConfigJsonEditor
                        config={config}
                        onConfigChange={onConfigChange}
                    />
                )}
            </StyledCardContent>
        </StyledCard>
    );
};
